/**
 * Shared GraphQL WebSocket connection status.
 * Updated by the graphql-ws client lifecycle in apolloClient; consumed by UI hooks.
 */

/** How many times to retry after a disconnect before going offline. */
export const WS_RETRY_ATTEMPTS = 3

/** Wait between retry attempts (3 × 3s ≈ 9s total repair window). */
export const WS_RETRY_WAIT_MS = 3000

export type WsConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline'

export type WsConnectionState = {
  status: WsConnectionStatus
  /** 1-based attempt while reconnecting; 0 otherwise */
  attempt: number
  maxAttempts: number
}

export const initialWsConnectionState: WsConnectionState = {
  status: 'connecting',
  attempt: 0,
  maxAttempts: WS_RETRY_ATTEMPTS,
}

type Listener = (state: WsConnectionState) => void

let state: WsConnectionState = { ...initialWsConnectionState }
const listeners = new Set<Listener>()

export function getWsConnectionState(): WsConnectionState {
  return state
}

export function setWsConnectionState(next: WsConnectionState): void {
  state = next
  listeners.forEach(listener => listener(state))
}

export function subscribeWsConnectionState(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Close codes that should not be retried (auth / policy failures). */
export const WS_DO_NOT_RETRY_CODES = new Set([1008, 1011, 4401, 4403])
